<script setup lang="ts">
import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'

type ExpirationMode = 'point_relative' | 'after_registration' | 'absolute_date'
type SelectionMode = ExpirationMode | 'disabled'
type PeriodUnit = 'day' | 'month' | 'year'

const DEFAULT_DURATION = 12
const DEFAULT_UNIT: PeriodUnit = 'month'
const DEFAULT_ABSOLUTE_DATE = '12-31'
const MONTH_DAY_REGEX = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
const MONTH_VALUES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const

const DAYS_PER_YEAR = 365
const MONTHS_PER_YEAR = 12
const DAYS_PER_MONTH = DAYS_PER_YEAR / MONTHS_PER_YEAR

const toDays = (amount: number, unit: PeriodUnit): number => {
  if (unit === 'year') return amount * DAYS_PER_YEAR
  if (unit === 'month') return amount * DAYS_PER_MONTH
  return amount
}

const fromDays = (days: number, unit: PeriodUnit): number => {
  if (unit === 'year') return days / DAYS_PER_YEAR
  if (unit === 'month') return days / DAYS_PER_MONTH
  return days
}

// Keeps the represented duration equal across units (e.g. 1 year -> 12 months -> 365 days).
const convertDuration = (amount: number, fromUnit: PeriodUnit, toUnit: PeriodUnit): number => {
  if (fromUnit === toUnit) return amount
  return Math.max(1, Math.round(fromDays(toDays(amount, fromUnit), toUnit)))
}

const { t, locale } = useI18n()

const pointExpirationType = defineModel<'point_relative' | 'after_registration' | 'absolute_date' | null>(
  'pointExpirationType',
  { default: null }
)
const pointExpirationUnit = defineModel<PeriodUnit | null>('pointExpirationUnit', { default: null })
const pointExpirationValue = defineModel<number | string | null>('pointExpirationValue', { default: null })

const props = defineProps<{
  // Saved rule only applies to points earned from this date onwards (null: applies to all points).
  pointExpirationStartsAt?: string | null
}>()

const parseValue = (value: number | string | null | undefined): number | null => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) || num < 1 ? null : num
}

const parseMonthDay = (value: string | null | undefined): string | null => {
  if (value == null || value === '') return null
  const trimmed = String(value).trim()
  if (MONTH_DAY_REGEX.test(trimmed)) return trimmed
  const isoMatch = trimmed.match(/^\d{4}-(\d{2})-(\d{2})/)
  if (isoMatch) {
    const monthDay = `${isoMatch[1]}-${isoMatch[2]}`
    return MONTH_DAY_REGEX.test(monthDay) ? monthDay : null
  }
  return null
}

const pad2 = (value: number): string => String(value).padStart(2, '0')

const daysInMonth = (month: number): number => new Date(2024, month, 0).getDate()

const nextOccurrence = (monthDay: string): Date => {
  const [month, day] = monthDay.split('-').map(Number)
  const now = new Date()
  const year = now.getFullYear()
  const lastDayThisYear = new Date(year, month, 0).getDate()
  const candidate = new Date(year, month - 1, Math.min(day, lastDayThisYear))
  const startOfToday = new Date(year, now.getMonth(), now.getDate())

  if (candidate >= startOfToday) return candidate

  const nextYear = year + 1
  const lastDayNextYear = new Date(nextYear, month, 0).getDate()
  return new Date(nextYear, month - 1, Math.min(day, lastDayNextYear))
}

const isExpirationMode = (value: unknown): value is ExpirationMode =>
  value === 'point_relative' || value === 'after_registration' || value === 'absolute_date'

const parsedType = computed<ExpirationMode | null>(() => {
  const raw = pointExpirationType.value
  return isExpirationMode(raw) ? raw : null
})
const parsedUnit = computed<PeriodUnit | null>(() => {
  const raw = pointExpirationUnit.value
  return raw === 'day' || raw === 'month' || raw === 'year' ? raw : null
})
const parsedDate = computed(() =>
  parsedType.value === 'absolute_date' ? parseMonthDay(pointExpirationValue.value as string | null) : null
)
const parsedAmount = computed(() =>
  parsedType.value != null && parsedType.value !== 'absolute_date' && parsedUnit.value != null
    ? parseValue(pointExpirationValue.value)
    : null
)
const isEnabled = computed(() => parsedDate.value != null || parsedAmount.value != null)

const userSelectionMode = ref<SelectionMode | null>(null)
const duration = ref<number | string | null>(DEFAULT_DURATION)
const periodUnit = ref<PeriodUnit>(DEFAULT_UNIT)
const absoluteDate = ref(DEFAULT_ABSOLUTE_DATE)

watch(
  parsedDate,
  (date) => {
    if (date) absoluteDate.value = date
  },
  { immediate: true }
)

watch(
  [parsedAmount, parsedUnit],
  ([amount, unit]) => {
    if (amount == null || unit == null) return
    duration.value = amount
    periodUnit.value = unit
  },
  { immediate: true }
)

const selectionMode = computed<SelectionMode>(() => {
  if (userSelectionMode.value) return userSelectionMode.value
  if (parsedType.value === 'absolute_date') return 'absolute_date'
  if (parsedType.value === 'after_registration') return 'after_registration'
  if (isEnabled.value) return 'point_relative'
  return 'disabled'
})

const selectionOptions = computed<RadioCardOption[]>(() => [
  {
    id: 'disabled',
    value: 'disabled',
    title: t('pages.loyaltyPage.tabs.pointExpiration.noExpiration.title'),
    description: t('pages.loyaltyPage.tabs.pointExpiration.noExpiration.description')
  },
  {
    id: 'point_relative',
    value: 'point_relative',
    title: t('pages.loyaltyPage.tabs.pointExpiration.relative.title'),
    description: t('pages.loyaltyPage.tabs.pointExpiration.relative.description')
  },
  {
    id: 'after_registration',
    value: 'after_registration',
    title: t('pages.loyaltyPage.tabs.pointExpiration.afterRegistration.title'),
    description: t('pages.loyaltyPage.tabs.pointExpiration.afterRegistration.description')
  },
  {
    id: 'absolute_date',
    value: 'absolute_date',
    title: t('pages.loyaltyPage.tabs.pointExpiration.absolute.title'),
    description: t('pages.loyaltyPage.tabs.pointExpiration.absolute.description')
  }
])

const ensureDurationDefaults = () => {
  if (duration.value === '' || duration.value == null) {
    duration.value = DEFAULT_DURATION
    periodUnit.value = DEFAULT_UNIT
  }
}

const writePeriodToModel = (type: 'point_relative' | 'after_registration') => {
  ensureDurationDefaults()

  const amount = Number(duration.value)
  if (Number.isNaN(amount) || amount < 1) {
    pointExpirationType.value = null
    pointExpirationUnit.value = null
    pointExpirationValue.value = null
    return
  }

  // Keep enabled while switching modes: write the active values before clearing the others.
  pointExpirationType.value = type
  pointExpirationUnit.value = periodUnit.value
  pointExpirationValue.value = Math.floor(amount)
}

const writeAbsoluteToModel = () => {
  if (!parseMonthDay(absoluteDate.value)) {
    absoluteDate.value = DEFAULT_ABSOLUTE_DATE
  }
  // Keep enabled while switching modes: write the active values before clearing the others.
  pointExpirationType.value = 'absolute_date'
  pointExpirationValue.value = absoluteDate.value
  pointExpirationUnit.value = null
}

const syncModelsForCurrentType = () => {
  if (selectionMode.value === 'absolute_date') {
    writeAbsoluteToModel()
    return
  }
  if (selectionMode.value === 'disabled') return
  writePeriodToModel(selectionMode.value)
}

const selectedExpirationOption = computed<RadioCardOption>({
  get: () => selectionOptions.value.find((option) => option.value === selectionMode.value) ?? selectionOptions.value[0],
  set: (option) => {
    if (option?.value === 'disabled') {
      userSelectionMode.value = 'disabled'
      pointExpirationType.value = null
      pointExpirationUnit.value = null
      pointExpirationValue.value = null
      return
    }
    if (!isExpirationMode(option?.value)) return
    userSelectionMode.value = option.value
    syncModelsForCurrentType()
  }
})

const unitOptions: { value: PeriodUnit; label: string }[] = [
  { value: 'day', label: t('global.words.days') },
  { value: 'month', label: t('global.words.month') },
  { value: 'year', label: t('global.words.year') }
]

const selectedMonth = computed(() => (parseMonthDay(absoluteDate.value) ?? DEFAULT_ABSOLUTE_DATE).slice(0, 2))
const selectedDay = computed(() => (parseMonthDay(absoluteDate.value) ?? DEFAULT_ABSOLUTE_DATE).slice(3, 5))

const monthOptions = computed(() =>
  MONTH_VALUES.map((value) => ({
    value,
    label: t(`global.months.${value}`)
  }))
)

const dayOptions = computed(() => {
  const maxDay = daysInMonth(Number(selectedMonth.value))
  return Array.from({ length: maxDay }, (_, index) => {
    const value = pad2(index + 1)
    return { value, label: String(index + 1) }
  })
})

const setAbsoluteMonthDay = (month: string, day: string) => {
  const maxDay = daysInMonth(Number(month))
  const parsedDay = Number(day)
  const clampedDay = pad2(Number.isNaN(parsedDay) ? 1 : Math.min(parsedDay, maxDay))
  absoluteDate.value = `${month}-${clampedDay}`
  if (selectionMode.value === 'absolute_date') {
    writeAbsoluteToModel()
  }
}

const handleMonthChange = (value: unknown) => {
  if (typeof value !== 'string' || !/^(0[1-9]|1[0-2])$/.test(value)) return
  setAbsoluteMonthDay(value, selectedDay.value)
}

const handleDayChange = (value: unknown) => {
  if (typeof value !== 'string' || !/^(0[1-9]|[12]\d|3[01])$/.test(value)) return
  setAbsoluteMonthDay(selectedMonth.value, value)
}

const formatLongDate = (date: Date) =>
  date.toLocaleDateString(locale.value, { day: 'numeric', month: 'long', year: 'numeric' })

const formatLongDateTime = (date: Date) =>
  date.toLocaleString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

// Points always expire at the end of their cutoff day, matching the backend's endOfDay() rule.
const endOfDay = (date: Date): Date => {
  const result = new Date(date)
  result.setHours(23, 59, 0, 0)
  return result
}

const addPeriod = (date: Date, unit: PeriodUnit, amount: number): Date => {
  const result = new Date(date)
  if (unit === 'year') {
    result.setFullYear(result.getFullYear() + amount)
  } else if (unit === 'month') {
    result.setMonth(result.getMonth() + amount)
  } else {
    result.setDate(result.getDate() + amount)
  }
  return result
}

const exampleText = computed(() => {
  if (selectionMode.value === 'disabled') return null

  if (selectionMode.value === 'absolute_date') {
    return t('pages.loyaltyPage.tabs.pointExpiration.exampleAbsolute', {
      expiresAt: formatLongDateTime(endOfDay(nextOccurrence(absoluteDate.value)))
    })
  }

  const amount = Number(duration.value)
  if (!amount || amount < 1) return null

  const startAt = new Date()
  const expiresAt = endOfDay(addPeriod(startAt, periodUnit.value, amount))

  if (selectionMode.value === 'after_registration') {
    return t('pages.loyaltyPage.tabs.pointExpiration.exampleAfterRegistration', {
      registeredAt: formatLongDate(startAt),
      expiresAt: formatLongDateTime(expiresAt)
    })
  }

  return t('pages.loyaltyPage.tabs.pointExpiration.example', {
    earnedAt: formatLongDate(startAt),
    expiresAt: formatLongDateTime(expiresAt)
  })
})

const startsAtText = computed(() => {
  if (!props.pointExpirationStartsAt || selectionMode.value === 'disabled') return null
  const startsAt = new Date(props.pointExpirationStartsAt)
  if (Number.isNaN(startsAt.getTime())) return null
  return t('pages.loyaltyPage.tabs.pointExpiration.startsAtTip', { date: formatLongDate(startsAt) })
})

const handleDurationChange = (value: string | number) => {
  duration.value = value
  // Ignore unmount/noise updates from period inputs after switching mode.
  if (selectionMode.value === 'absolute_date' || selectionMode.value === 'disabled') return
  syncModelsForCurrentType()
}

const handleUnitChange = (value: unknown) => {
  if (value !== 'day' && value !== 'month' && value !== 'year') return

  const amount = parseValue(duration.value)
  if (amount != null) {
    duration.value = convertDuration(amount, periodUnit.value, value)
  }
  periodUnit.value = value

  if (selectionMode.value === 'absolute_date' || selectionMode.value === 'disabled') return
  syncModelsForCurrentType()
}
</script>

<template>
  <TabSubPanel
    :section-main-title="t('pages.loyaltyPage.tabs.pointExpiration.sectionTitle')"
    :section-main-subtitle="t('pages.loyaltyPage.tabs.pointExpiration.sectionSubtitle')"
  >
    <template #content>
      <AppRadioCard v-model="selectedExpirationOption" :options="selectionOptions" class="grid grid-cols-1 gap-3">
        <template #1="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="text-sm font-medium text-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.relative.title') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.relative.description') }}
            </span>
            <div v-if="checked" class="mt-3 space-y-3" @click.stop>
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm text-foreground-secondary">
                  {{ t('pages.loyaltyPage.tabs.pointExpiration.expirePrefix') }}
                </span>
                <AppFormInput
                  id="point-expiration-duration"
                  :model-value="duration ?? ''"
                  type="number"
                  :min="1"
                  class="w-20"
                  @update:model-value="handleDurationChange"
                />
                <Select :model-value="periodUnit" @update:model-value="handleUnitChange">
                  <SelectTrigger class="h-10 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="option in unitOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <span class="text-sm text-foreground-secondary">
                  {{ t('pages.loyaltyPage.tabs.pointExpiration.expireSuffix') }}
                </span>
              </div>
              <BaseTip v-if="exampleText" :text="exampleText" severity="info" />
            </div>
          </div>
        </template>

        <template #2="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="text-sm font-medium text-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.afterRegistration.title') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.afterRegistration.description') }}
            </span>
            <div v-if="checked" class="mt-3 space-y-3" @click.stop>
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm text-foreground-secondary">
                  {{ t('pages.loyaltyPage.tabs.pointExpiration.expirePrefix') }}
                </span>
                <AppFormInput
                  id="point-expiration-duration-after-registration"
                  :model-value="duration ?? ''"
                  type="number"
                  :min="1"
                  class="w-20"
                  @update:model-value="handleDurationChange"
                />
                <Select :model-value="periodUnit" @update:model-value="handleUnitChange">
                  <SelectTrigger class="h-10 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="option in unitOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <span class="text-sm text-foreground-secondary">
                  {{ t('pages.loyaltyPage.tabs.pointExpiration.expireAfterRegistrationSuffix') }}
                </span>
              </div>
              <BaseTip v-if="exampleText" :text="exampleText" severity="info" />
            </div>
          </div>
        </template>

        <template #3="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="text-sm font-medium text-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.absolute.title') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.loyaltyPage.tabs.pointExpiration.absolute.description') }}
            </span>
            <div v-if="checked" class="mt-3 space-y-3" @click.stop>
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm text-foreground-secondary">
                  {{ t('pages.loyaltyPage.tabs.pointExpiration.expireAbsolutePrefix') }}
                </span>
                <Select :model-value="selectedDay" @update:model-value="handleDayChange">
                  <SelectTrigger id="point-expiration-day" class="h-10 w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="option in dayOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select :model-value="selectedMonth" @update:model-value="handleMonthChange">
                  <SelectTrigger id="point-expiration-month" class="h-10 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="option in monthOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <BaseTip v-if="exampleText" :text="exampleText" severity="info" />
            </div>
          </div>
        </template>
      </AppRadioCard>
      <BaseTip v-if="startsAtText" :text="startsAtText" severity="secondary" class="mt-3" data-test="starts-at-tip" />
    </template>
  </TabSubPanel>
</template>
